import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
interface FaqItem {
  question: string;
  answer: string | React.ReactNode;
}
const faqs: FaqItem[] = [{
  question: 'What areas do you deliver to?',
  answer: <>
        We deliver throughout Tacoma, Puyallup, Federal Way, Auburn, Kent,
        Bonney Lake, and surrounding King County areas.{' '}
        <Link to="/delivery-zones" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-454">
          View our full delivery zone map →
        </Link>
      </>
}, {
  question: 'What is the minimum order?',
  answer: 'Our minimum order is 5 cubic yards for all materials.'
}, {
  question: 'Do you offer same-day delivery?',
  answer: 'Yes! We offer same-day delivery for orders placed before 10 AM, subject to availability.'
}, {
  question: 'How do I know how much material I need?',
  answer: <>
        Use our{' '}
        <Link to="/help" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-455">
          Material Calculator & Quiz
        </Link>{' '}
        to estimate based on your project dimensions, or{' '}
        <Link to="/compare" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-456">
          compare materials
        </Link>{' '}
        side by side. You can also give us a call and we'll help you figure it
        out.
      </>
}, {
  question: 'What forms of payment do you accept?',
  answer: 'We accept cash, check, and all major credit cards. Payment is due upon delivery.'
}, {
  question: 'Can I pick up materials instead of delivery?',
  answer: 'We are a delivery-only service at this time. All materials are delivered directly to your location by our professional drivers.'
}, {
  question: 'Do you spread the gravel after delivery?',
  answer: <>
        It depends on the situation. In some cases we can spread the material
        depending on access, terrain, and the type of project.{' '}
        <a href="tel:206-619-3742" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-457">
          Give us a call
        </a>{' '}
        to discuss your specific setup. Check out our{' '}
        <Link to="/delivery-prep" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-458">
          Delivery Prep Guide
        </Link>{' '}
        to get your site ready.
      </>
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [lastClosedIndex, setLastClosedIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    if (openIndex === index) {
      // Closing this question — mark it as last closed
      setLastClosedIndex(index);
      setOpenIndex(null);
    } else {
      // Opening a new question — clear last closed
      setLastClosedIndex(null);
      setOpenIndex(index);
    }
  };
  return <section className="py-16 sm:py-24 bg-[#f3f0ec] dark:bg-[#292827] transition-colors duration-300 scroll-mt-32 border-t border-stone-400 dark:border-transparent" id="faq" data-id="element-459">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mb-12 sm:mb-20 text-center" data-id="element-460">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} data-id="element-461">
          <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-6" data-id="element-462">
            Da FAQs
          </h2>
          <p className="text-lg sm:text-xl text-stone-700 dark:text-stone-200 max-w-2xl mx-auto" data-id="element-463">
            Quick answers about delivery, materials & pricing. If your question
            isn't listed below, just give us a call.
          </p>
        </motion.div>
      </div>

      {/* FAQ List - Full Width */}
      <div className="w-full" data-id="element-464">
        {faqs.map((faq, index) => {
        const isOpen = openIndex === index;
        return <motion.div key={index} initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05,
          duration: 0.3
        }} className={`w-full transition-colors duration-300 ${isOpen ? 'bg-[#006241]' : 'bg-transparent'}`} data-id="element-465">
              <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-466">
                <button onClick={() => toggleFAQ(index)} className="w-full flex items-start justify-between gap-6 py-8 sm:py-12 text-left group" aria-expanded={isOpen} aria-controls={`faq-answer-${index}`} data-id="element-467">
                  <span className={`font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight transition-colors duration-300 ${isOpen ? 'text-white' : lastClosedIndex === index ? 'text-[#2A9D6F] dark:text-[#2A9D6F] group-hover:text-[#238A5F] dark:group-hover:text-[#238A5F]' : 'text-stone-900 dark:text-white group-hover:text-[#2A9D6F] dark:group-hover:text-[#2A9D6F]'}`} data-id="element-468">
                    {faq.question}
                  </span>
                  <motion.span animate={{
                rotate: isOpen ? 45 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className={`shrink-0 mt-1 sm:mt-2 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-469">
                    <Plus className="w-8 h-8 sm:w-12 sm:h-12 stroke-[1]" data-id="element-470" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-471">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" id={`faq-answer-${index}`} data-id="element-472">
                      <p className="pb-12 sm:pb-16 text-lg sm:text-xl md:text-2xl text-white/95 leading-relaxed max-w-4xl font-medium" data-id="element-473">
                        {faq.answer}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Still Have Questions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 sm:mt-10" data-id="element-474">
        <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left py-4 gap-3 sm:gap-5" data-id="element-475">
          <MessageCircle className="w-5 h-5 text-[#006241] shrink-0" data-id="element-476" />
          <p className="text-stone-900 dark:text-white font-heading font-extrabold text-lg sm:text-xl" data-id="element-477">
            Still have questions?
            <span className="font-normal text-stone-600 dark:text-stone-400 text-base ml-2" data-id="element-478">
              Shoot us a text.
            </span>
          </p>
          <a href="sms:206-619-3742" className="inline-flex items-center justify-center px-6 py-2.5 bg-[#00754d] hover:bg-[#006241] text-white rounded-full font-bold text-sm transition-colors" data-id="element-479">
            Text Us
          </a>
        </div>
      </div>
    </section>;
}